import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { REFERRAL_CHANNEL } from '@/configs'
import { PageInfo, PaginationResponse } from '../model'
import {
  Campaign,
  CampaignAttributedLead,
  CampaignFunnel,
  CampaignListFilter,
  CampaignReportFilter,
  CampaignReportResult,
  CampaignSpend,
  CampaignStatusFilter,
  CreateCampaignPayload,
  CreateSpendPayload,
  QuickCreateCampaignPayload,
  UpdateCampaignPayload,
  UpdateSpendPayload,
} from './model'

type Dict = Record<string, unknown>

export const CampaignService = (client: AxiosInstance) => {
  return {
    async list(filter: CampaignListFilter = {}): Promise<PaginationResponse<Campaign>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Dict; data: Dict[] }>('/campaigns', {
          params: {
            page: filter.page,
            pageSize: filter.pageSize,
            query: filter.query || undefined,
            channel: filter.channel,
            // Backend expects a concrete CampaignStatus; ALL means "no filter".
            status:
              filter.status && filter.status !== CampaignStatusFilter.ALL
                ? filter.status
                : undefined,
          },
        })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, Campaign)),
      }
    },

    async listActive(channel?: REFERRAL_CHANNEL): Promise<Campaign[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Dict[]>('/campaigns/active', { params: { channel } })
      )
      return (data ?? []).map((item) => deserialize(item, Campaign))
    },

    async get(id: string): Promise<Campaign> {
      const { data } = await wrapApiCall(() => client.get<Dict>(`/campaigns/${id}`))
      return deserialize(data, Campaign)
    },

    async create(payload: CreateCampaignPayload): Promise<Campaign> {
      const { data } = await wrapApiCall(() => client.post<Dict>('/campaigns', payload))
      return deserialize(data, Campaign)
    },

    async quickCreate(payload: QuickCreateCampaignPayload): Promise<Campaign> {
      const { data } = await wrapApiCall(() => client.post<Dict>('/campaigns/quick', payload))
      return deserialize(data, Campaign)
    },

    async update(id: string, payload: UpdateCampaignPayload): Promise<Campaign> {
      const { data } = await wrapApiCall(() => client.patch<Dict>(`/campaigns/${id}`, payload))
      return deserialize(data, Campaign)
    },

    async end(id: string): Promise<Campaign> {
      const { data } = await wrapApiCall(() => client.patch<Dict>(`/campaigns/${id}/end`))
      return deserialize(data, Campaign)
    },

    // --- Spend log ---
    async listSpends(campaignId: string): Promise<CampaignSpend[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Dict[]>(`/campaigns/${campaignId}/spends`)
      )
      return (data ?? []).map((item) => deserialize(item, CampaignSpend))
    },

    async createSpend(campaignId: string, payload: CreateSpendPayload): Promise<CampaignSpend> {
      const { data } = await wrapApiCall(() =>
        client.post<Dict>(`/campaigns/${campaignId}/spends`, payload)
      )
      return deserialize(data, CampaignSpend)
    },

    async updateSpend(id: string, payload: UpdateSpendPayload): Promise<CampaignSpend> {
      const { data } = await wrapApiCall(() =>
        client.patch<Dict>(`/campaigns/spends/${id}`, payload)
      )
      return deserialize(data, CampaignSpend)
    },

    async deleteSpend(id: string): Promise<{ success: boolean }> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success: boolean }>(`/campaigns/spends/${id}`)
      )
      return { success: data?.success ?? true }
    },

    // --- Funnel + attributed leads ---
    async getFunnel(campaignId: string): Promise<CampaignFunnel> {
      const { data } = await wrapApiCall(() => client.get<Dict>(`/campaigns/${campaignId}/funnel`))
      return deserialize(data, CampaignFunnel)
    },

    async listAttributedLeads(
      campaignId: string,
      opt: { page?: number; pageSize?: number } = {}
    ): Promise<PaginationResponse<CampaignAttributedLead>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Dict; data: Dict[] }>(`/campaigns/${campaignId}/leads`, {
          params: { page: opt.page, pageSize: opt.pageSize },
        })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, CampaignAttributedLead)),
      }
    },

    // --- Aggregated report ---
    async report(filter: CampaignReportFilter = {}): Promise<CampaignReportResult> {
      const { data } = await wrapApiCall(() =>
        client.get<Dict>('/campaigns/report', {
          params: {
            startDate: filter.startDate,
            endDate: filter.endDate,
            channel: filter.channel,
            campaignId: filter.campaignId,
          },
        })
      )
      return deserialize(data, CampaignReportResult)
    },
  }
}
